import { useId, type ReactNode } from 'react';
import type { CharacterAttributes, CharacterSlotType } from '@thottbot/shared';
import { ArtifactLink, TooltipAnchor } from '../../components/ArtifactLink';

/** The game's character sheet layout: eight slots down the left, eight down the right, weapons below. */
const LEFT: CharacterSlotType[] = ['HEAD', 'NECK', 'SHOULDER', 'BACK', 'CHEST', 'SHIRT', 'TABARD', 'WRIST'];
const RIGHT: CharacterSlotType[] = ['HANDS', 'WAIST', 'LEGS', 'FEET', 'FINGER_1', 'FINGER_2', 'TRINKET_1', 'TRINKET_2'];
const BOTTOM: CharacterSlotType[] = ['MAIN_HAND', 'OFF_HAND', 'RANGED', 'AMMO'];

export interface PaperDollSlot {
  slot: CharacterSlotType;
  label: string;
  item: { name: string; slug?: string; iconUrl?: string; quality?: number } | null;
  /** The item's own lines (item level, armor, stats...): the tooltip body when it has no page to fetch. */
  lines: string[];
  /** Notes about this slot rather than the item (enchant, source, alternative...), under the tooltip body. */
  contextLines?: string[];
  /** The game's empty-slot art for this slot. */
  artUrl: string;
  /** A control beside the slot, such as a build owner's menu of other items. */
  menu?: ReactNode;
}

function Slot({ s }: { s: PaperDollSlot }) {
  const detailsId = useId();
  const item = s.item;
  const quality = item?.quality ?? 1;
  const context = s.contextLines ?? [];
  const inner = item ? (
    item.iconUrl ? (
      <img src={item.iconUrl} alt="" />
    ) : (
      <span className="sheet-slot-text">{item.name.slice(0, 2)}</span>
    )
  ) : (
    // The game's own empty-slot art (client media); vacant slots stay unlabelled images.
    <img className="sheet-slot-art" src={s.artUrl} alt="" />
  );
  // Hover and focus open the game tooltip; screen readers also get every line as a description,
  // since the fetched tooltip only exists while open.
  const described = item && s.lines.length + context.length > 0 ? detailsId : undefined;
  const label = item ? `${s.label}: ${item.name}` : `${s.label}: empty`;
  const box = <span className={`sheet-slot${item ? ` quality-border-${quality}` : ' sheet-slot-vacant'}`}>{inner}</span>;
  return (
    <li className="sheet-slot-row" data-testid="sheet-slot" data-slot={s.slot} data-filled={!!item}>
      {item?.slug ? (
        <ArtifactLink
          type="item"
          slug={item.slug}
          name={item.name}
          quality={item.quality}
          className=""
          contextLines={context}
          aria-label={label}
          aria-describedby={described}
        >
          {box}
        </ArtifactLink>
      ) : (
        <TooltipAnchor
          known={item ? { name: item.name, quality: item.quality } : { name: s.label }}
          lines={item ? s.lines : ['Empty']}
          contextLines={context}
          aria-label={label}
          aria-describedby={described}
        >
          {box}
        </TooltipAnchor>
      )}
      {described && (
        <span id={described} className="sr-only">
          {[...s.lines, ...context].join('. ')}
        </span>
      )}
      {s.menu}
    </li>
  );
}

function Attribute({ label, value }: { label: string; value: number }) {
  return (
    <tr>
      <th scope="row">{label}</th>
      <td>{value}</td>
    </tr>
  );
}

/**
 * The in-game character window: paper doll of slot icons around a name plate, attributes
 * beside it. Imported characters and generated builds both draw through it.
 */
export function PaperDoll({
  testId,
  title,
  guild,
  classIconUrl,
  classLabel,
  plate,
  slots,
  attributes,
  attributesNote,
  status,
}: {
  testId: string;
  title: string;
  guild?: string | null;
  classIconUrl: string;
  classLabel: string;
  plate: ReactNode;
  slots: PaperDollSlot[];
  attributes: CharacterAttributes;
  attributesNote: string;
  status?: ReactNode;
}) {
  const bySlot = new Map(slots.map((s) => [s.slot, s]));
  const rows = (list: CharacterSlotType[]) => list.map((s) => bySlot.get(s)).filter((s): s is PaperDollSlot => !!s);
  const a = attributes;
  return (
    <div className="sheet" data-testid={testId}>
      <div className="sheet-frame">
        <div className="sheet-title">
          {title}
          {guild && <span className="sheet-guild">&lt;{guild}&gt;</span>}
        </div>
        <div className="sheet-body">
          <ul className="sheet-column" aria-label="Left equipment slots">
            {rows(LEFT).map((s) => (
              <Slot key={s.slot} s={s} />
            ))}
          </ul>
          <div className="sheet-model">
            <img
              className="sheet-class-icon"
              src={classIconUrl}
              alt={`${classLabel} class icon`}
              onError={(e) => ((e.target as HTMLImageElement).style.visibility = 'hidden')}
            />
            <div className="sheet-plate">{plate}</div>
          </div>
          <ul className="sheet-column" aria-label="Right equipment slots">
            {rows(RIGHT).map((s) => (
              <Slot key={s.slot} s={s} />
            ))}
          </ul>
          <div className="sheet-stats" data-testid="sheet-attributes">
            <div className="sheet-stats-title">Attributes</div>
            <table className="sheet-stats-table">
              <tbody>
                <Attribute label="Strength" value={a.strength} />
                <Attribute label="Agility" value={a.agility} />
                <Attribute label="Stamina" value={a.stamina} />
                <Attribute label="Intellect" value={a.intellect} />
                <Attribute label="Spirit" value={a.spirit} />
                <Attribute label="Armor" value={a.armor} />
              </tbody>
            </table>
            <div className="muted sheet-stats-note">{attributesNote}</div>
          </div>
        </div>
        <ul className="sheet-weapons" aria-label="Weapon slots">
          {rows(BOTTOM).map((s) => (
            <Slot key={s.slot} s={s} />
          ))}
        </ul>
        <div className="sheet-status">{status}</div>
      </div>
    </div>
  );
}
